// src/services/protocolComparePdf.js
//
// Protocol Compare's "Download PDF" for the AI summary and the redline, so an
// admin can share them with staff. Drawn with jsPDF by hand, like
// potentialPatientsPdf.js. Built in the browser from what the pop-up already
// holds; nothing is uploaded. No Firebase, no React: tested on its own.
//
// jsPDF's built-in font only has the Windows-1252 characters, so anything else
// is written out (≥ as ">=", α as "alpha") by pdfSafe(), never dropped
// silently. The footer says so.

import { jsPDF } from "jspdf";

const MARGIN = 48;
const FOOT = 36;

// Windows-1252 beyond Latin-1: what the built-in font can draw.
const CP1252_EXTRA = new Set("€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ");
const SPELLED = {
  "≥": ">=", "≤": "<=", "≠": "!=", "≈": "~", "→": "->", "←": "<-", "↑": "^", "↓": "v", "⇒": "=>",
  "✓": "[x]", "✔": "[x]", "✗": "x", "✘": "x", "−": "-", "‐": "-", "‑": "-", "‒": "-", "―": "-",
  "′": "'", "″": "\"", "∞": "infinity", "√": "sqrt", "∑": "sum", "∆": "delta", "Δ": "Delta",
  "α": "alpha", "β": "beta", "γ": "gamma", "δ": "delta", "ε": "epsilon", "κ": "kappa", "λ": "lambda",
  "μ": "µ", "π": "pi", "σ": "sigma", "τ": "tau", "ω": "omega", "Ω": "Ohm", " ": " ", " ": " ", " ": " ",
  "​": "", "﻿": "",
};

/** Text the built-in PDF font can draw: symbols outside it are written out; anything else becomes "?". */
export const pdfSafe = (text) =>
  Array.from(String(text == null ? "" : text))
    .map((ch) => {
      if (SPELLED[ch] !== undefined) return SPELLED[ch];
      const c = ch.codePointAt(0);
      if (c === 10 || (c >= 0x20 && c <= 0x7e) || (c >= 0xa0 && c <= 0xff) || CP1252_EXTRA.has(ch)) return ch;
      return "?";
    })
    .join("");

const fmtDate = (d = new Date()) =>
  d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const baseName = (f) => String(f || "").replace(/\.pdf$/i, "") || "—";

/** Shared page frame: header block, footer on every page, page breaks. */
const makeDoc = ({ title, subtitle, footerNote }) => {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const st = { doc, pageW, pageH, usableW: pageW - MARGIN * 2, y: MARGIN };
  st.newPage = () => {
    doc.addPage();
    st.y = MARGIN;
  };
  st.ensure = (h) => {
    if (st.y + h > pageH - FOOT - 12) st.newPage();
  };
  st.para = (text, { size = 10, style = "normal", color = 20, gap = 4, indent = 0 } = {}) => {
    doc.setFont("helvetica", style).setFontSize(size).setTextColor(color);
    const lines = doc.splitTextToSize(pdfSafe(text), st.usableW - indent);
    const lh = size * 1.3;
    for (const line of lines) {
      st.ensure(lh);
      doc.text(line, MARGIN + indent, st.y + size);
      st.y += lh;
    }
    st.y += gap;
  };
  st.finish = () => {
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i += 1) {
      doc.setPage(i);
      doc.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(120);
      doc.text(pdfSafe(footerNote), MARGIN, pageH - 20, { maxWidth: st.usableW - 60 });
      doc.text(`Page ${i} of ${n}`, pageW - MARGIN, pageH - 20, { align: "right" });
    }
    doc.setTextColor(0);
    return doc;
  };
  st.para(title, { size: 16, style: "bold", gap: 2 });
  if (subtitle) st.para(subtitle, { size: 10, color: 90, gap: 10 });
  return st;
};

/** A boxed, amber notice at the current position (used for the AI warning). */
const aiBox = (st, text) => {
  const { doc } = st;
  doc.setFont("helvetica", "bold").setFontSize(9.5);
  const lines = doc.splitTextToSize(pdfSafe(text), st.usableW - 20);
  const h = lines.length * 12.5 + 14;
  st.ensure(h + 8);
  doc.setFillColor(255, 251, 235).setDrawColor(217, 119, 6).setLineWidth(0.8);
  doc.rect(MARGIN, st.y, st.usableW, h, "FD");
  doc.setTextColor(146, 64, 14);
  lines.forEach((l, i) => doc.text(l, MARGIN + 10, st.y + 17 + i * 12.5));
  doc.setTextColor(0);
  st.y += h + 12;
};

const headerLines = ({ study, comparison, generatedBy }) => {
  const studyName = study?.NAME || study?.STUDY_ID || "";
  return [
    studyName && `Study: ${studyName}`,
    `From: ${baseName(comparison?.old?.fileName)}`,
    `To: ${baseName(comparison?.new?.fileName)}`,
    `Made ${fmtDate()}${generatedBy ? ` by ${generatedBy}` : ""}`,
  ]
    .filter(Boolean)
    .join("\n");
};

const IMPACT_ORDER = { high: 0, medium: 1, low: 2 };
const CATEGORY_LABEL = {
  eligibility: "Eligibility",
  visits_schedule: "Visits & schedule",
  dosing_treatment: "Dosing & treatment",
  safety_reporting: "Safety reporting",
  procedures_assessments: "Procedures & assessments",
  endpoints_statistics: "Endpoints & statistics",
  administrative_editorial: "Administrative",
  other: "Other",
};

/** The AI summary as a PDF (high impact first). */
export const buildSummaryPdf = ({ study, comparison, generatedBy }) => {
  const s = comparison?.summary || {};
  const st = makeDoc({
    title: "Protocol changes: AI summary",
    subtitle: headerLines({ study, comparison, generatedBy }),
    footerNote: "AI-generated summary. AI can make mistakes: use it as a helper, not the final word, and check each change against the redline. Symbols the PDF font can't show are written out (e.g. >=).",
  });
  aiBox(st, "AI-generated summary. AI can make mistakes. Use this as a helper, not the final word: check each change against the redline (and the protocols) before relying on it.");
  if (s.overview) {
    st.para("Overview", { size: 12, style: "bold", gap: 2 });
    st.para(s.overview, { gap: 10 });
  }
  const changes = [...(s.changes || [])].sort((a, b) => (IMPACT_ORDER[a.impact] ?? 1) - (IMPACT_ORDER[b.impact] ?? 1));
  st.para(`Changes (${changes.length})`, { size: 12, style: "bold", gap: 4 });
  changes.forEach((c, i) => {
    st.ensure(60);
    st.para(`${i + 1}. ${c.title || "Change"}`, { size: 11, style: "bold", gap: 1 });
    const impact = c.impact ? `${c.impact[0].toUpperCase()}${c.impact.slice(1)} impact` : "";
    st.para([impact, CATEGORY_LABEL[c.category] || c.category, c.section].filter(Boolean).join(" · "), { size: 9, color: 100, gap: 2, indent: 12 });
    if (c.before) st.para(`Before: ${c.before}`, { size: 10, gap: 1, indent: 12 });
    if (c.after) st.para(`Now: ${c.after}`, { size: 10, gap: 1, indent: 12 });
    if (c.siteAction) st.para(`What the site needs to do: ${c.siteAction}`, { size: 10, style: "bold", gap: 1, indent: 12 });
    st.y += 6;
  });
  const notes = [];
  if ((s.editorialHunkIds || []).length) notes.push(`${s.editorialHunkIds.length} editorial-only change(s) (wording, formatting, typos) are in the redline only.`);
  if ((s.unexplainedHunkIds || []).length) notes.push(`${s.unexplainedHunkIds.length} change(s) aren't covered by this summary; see the redline.`);
  if (notes.length) st.para(notes.join(" "), { size: 9, color: 100 });
  return st.finish();
};

// ---- Redline ----
const FOLD_OVER = 60;
const KEEP = 20;
const STATUS_LABEL = { changed: "Changed", added: "Added", removed: "Removed", moved: "Moved", same: "No change" };
const sectionName = (s) => {
  const h = s.new || s.old || {};
  return `${h.number ? `${h.number} ` : ""}${h.title || ""}`.trim() || "Untitled section";
};
const pageText = (s) => {
  const o = s.old?.page;
  const n = s.new?.page;
  if (o != null && n != null) return o === n ? `p.${n}` : `old p.${o}, new p.${n}`;
  if (n != null) return `new p.${n}`;
  if (o != null) return `old p.${o}`;
  return "";
};

/**
 * The redline as a PDF: removed words red and struck through, added words
 * green and underlined, long unchanged stretches shortened as on screen.
 * @param {{study, comparison, redline, onlyChanged?: boolean, generatedBy?: string}} args
 */
export const buildRedlinePdf = ({ study, comparison, redline, onlyChanged = true, generatedBy }) => {
  const st = makeDoc({
    title: "Protocol changes: redline",
    subtitle: headerLines({ study, comparison, generatedBy }),
    footerNote: "Red, struck through = removed. Green, underlined = added. Blue = moved without changes. Long unchanged passages are shortened. Symbols the PDF font can't show are written out (e.g. >=).",
  });
  st.para("Found by a text comparison of the two protocols, not by AI. Check changes marked [table] or [scanned] against the PDFs.", { size: 9, color: 90, gap: 6 });
  const { doc } = st;
  const stats = redline?.stats || {};
  st.para(
    `${stats.sectionsChanged || 0} sections changed, ${stats.sectionsAdded || 0} added, ${stats.sectionsRemoved || 0} removed · ${stats.wordsAdded || 0} words added, ${stats.wordsRemoved || 0} removed${stats.movedHunks ? ` · ${stats.movedHunks} passage(s) moved` : ""}${onlyChanged ? " · sections with no changes are left out" : ""}`,
    { size: 9, color: 90, gap: 10 }
  );
  const SIZE = 9.5;
  const LH = SIZE * 1.45;
  const COLORS = { eq: [30, 30, 30], del: [190, 18, 60], ins: [4, 120, 87], moved: [3, 105, 161], note: [120, 120, 120] };

  // Flowing text, word by word, so each run keeps its own colour and line.
  let x = MARGIN;
  const newLine = () => {
    st.y += LH;
    x = MARGIN;
    st.ensure(LH);
  };
  const word = (w, kind) => {
    const t = pdfSafe(w);
    if (!t) return;
    doc.setFont("helvetica", kind === "note" ? "italic" : "normal").setFontSize(kind === "note" ? 8 : SIZE);
    const width = doc.getTextWidth(t);
    const space = doc.getTextWidth(" ");
    if (x > MARGIN && x + width > MARGIN + st.usableW) newLine();
    const [r, g, b] = COLORS[kind];
    doc.setTextColor(r, g, b);
    const base = st.y + SIZE;
    doc.text(t, x, base);
    if (kind === "del" || kind === "ins") {
      doc.setDrawColor(r, g, b).setLineWidth(0.6);
      const ly = kind === "del" ? base - SIZE * 0.3 : base + 1.5;
      doc.line(x, ly, x + width, ly);
    }
    x += width + space;
  };
  const run = (text, kind) => String(text || "").split(/\s+/).filter(Boolean).forEach((w) => word(w, kind));

  const sections = (redline?.sections || []).filter((s) => !onlyChanged || s.status !== "same");
  if (!sections.length) st.para("No differences were found between the two protocols.");
  for (const s of sections) {
    st.ensure(40);
    st.para(`${sectionName(s)}  [${STATUS_LABEL[s.status] || s.status}]`, { size: 11, style: "bold", gap: 0 });
    const meta = [pageText(s), s.headingChanged && s.old ? `heading was "${`${s.old.number ? `${s.old.number} ` : ""}${s.old.title}`}"` : "", s.newHeading ? "new heading (text unchanged)" : ""].filter(Boolean).join(" · ");
    if (meta) st.para(meta, { size: 8.5, color: 110, gap: 2 });
    st.ensure(LH);
    x = MARGIN;
    const hunkAt = new Map((s.hunks || []).map((h) => [h.segment, h]));
    (s.segments || []).forEach((g, i) => {
      const h = hunkAt.get(i);
      if (h && h.table) run("[table: check both PDFs]", "note");
      if (h && h.ocr) run("[scanned: check the PDF]", "note");
      if (g.t === "mvout") {
        run(`[${g.n || ""} words moved${g.to != null ? ` to new p.${g.to}` : ""}]`, "note");
        return;
      }
      if (g.t === "mvin") {
        run(`[moved here${g.from != null ? ` from old p.${g.from}` : ""}:]`, "note");
        const words = String(g.x || "").split(" ");
        if (words.length > FOLD_OVER) {
          run(words.slice(0, KEEP).join(" "), "moved");
          run(`[... ${words.length - 2 * KEEP} more words ...]`, "note");
          run(words.slice(-KEEP).join(" "), "moved");
        } else run(g.x, "moved");
        return;
      }
      if (g.t === "eq") {
        const words = String(g.x || "").split(" ");
        if (words.length > FOLD_OVER) {
          run(words.slice(0, KEEP).join(" "), "eq");
          run(`[... ${words.length - 2 * KEEP} unchanged words ...]`, "note");
          run(words.slice(-KEEP).join(" "), "eq");
        } else run(g.x, "eq");
      } else run(g.x, g.t === "del" ? "del" : "ins");
    });
    st.y += LH + 8;
    doc.setTextColor(0);
  }
  return st.finish();
};

/** "protocol-summary_<study>_<date>.pdf" / "protocol-redline_<study>_<date>.pdf" */
export const comparePdfFileName = (kind, study) => {
  const idPart = String(study?.STUDY_ID || study?.NAME || "study").replace(/[^\w.-]+/g, "_");
  return `protocol-${kind}_${idPart}_${new Date().toISOString().slice(0, 10)}.pdf`;
};
